// Background Sync utility for PWA
// This module handles registering sync events and processing them when back online

const SYNC_TAG = 'vesper-script-sync';
const PERIODIC_SYNC_TAG = 'vesper-periodic-sync';
const PERIODIC_SYNC_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds (minimum allowed)

export interface BackgroundSyncManager {
  registerSync: () => Promise<boolean>;
  isSupported: () => boolean;
}

export interface PeriodicSyncStatus {
  isSupported: boolean;
  isRegistered: boolean;
  minInterval: number;
}

/**
 * Check if Background Sync API is supported
 */
export const isBackgroundSyncSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
};

/**
 * Check if Periodic Background Sync API is supported
 */
export const isPeriodicSyncSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'PeriodicSyncManager' in window;
};

/**
 * Register a background sync event
 * This will trigger sync when the device comes back online
 */
type SyncCapableRegistration = ServiceWorkerRegistration & {
  sync?: { register: (tag: string) => Promise<void> };
};

type PeriodicSyncCapableRegistration = ServiceWorkerRegistration & {
  periodicSync?: {
    register: (tag: string, options: { minInterval: number }) => Promise<void>;
    unregister: (tag: string) => Promise<void>;
    getTags: () => Promise<string[]>;
  };
};

export const registerBackgroundSync = async (): Promise<boolean> => {
  if (!isBackgroundSyncSupported()) {
    console.log('Background Sync not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if sync is available on the registration
    const syncRegistration = registration as SyncCapableRegistration;
    if (syncRegistration.sync) {
      await syncRegistration.sync.register(SYNC_TAG);
      console.log('Background sync registered');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Failed to register background sync:', error);
    return false;
  }
};

/**
 * Check if periodic sync is already registered
 */
export const isPeriodicSyncRegistered = async (): Promise<boolean> => {
  if (!isPeriodicSyncSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const periodicSync = (registration as PeriodicSyncCapableRegistration).periodicSync;
    
    if (periodicSync) {
      const tags = await periodicSync.getTags();
      return tags.includes(PERIODIC_SYNC_TAG);
    }
    
    return false;
  } catch (error) {
    console.error('Error checking periodic sync status:', error);
    return false;
  }
};

/**
 * Register for periodic background sync
 * This allows the app to sync at regular intervals even when closed
 */
export const registerPeriodicSync = async (): Promise<boolean> => {
  if (!isPeriodicSyncSupported()) {
    console.log('Periodic Background Sync not supported');
    return false;
  }

  try {
    // Check permission status first
    const status = await navigator.permissions.query({
      name: 'periodic-background-sync' as PermissionName,
    });

    if (status.state !== 'granted') {
      console.log('Periodic sync permission not granted:', status.state);
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const periodicSync = (registration as PeriodicSyncCapableRegistration).periodicSync;

    if (periodicSync) {
      await periodicSync.register(PERIODIC_SYNC_TAG, {
        minInterval: PERIODIC_SYNC_INTERVAL, // Minimum 1 hour
      });
      console.log('Periodic background sync registered');
      
      // Store registration status
      localStorage.setItem('periodic-sync-enabled', 'true');
      
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to register periodic sync:', error);
    return false;
  }
};

/**
 * Unregister periodic background sync
 */
export const unregisterPeriodicSync = async (): Promise<boolean> => {
  if (!isPeriodicSyncSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const periodicSync = (registration as PeriodicSyncCapableRegistration).periodicSync;

    if (periodicSync) {
      await periodicSync.unregister(PERIODIC_SYNC_TAG);
      console.log('Periodic background sync unregistered');
      
      // Remove registration status
      localStorage.removeItem('periodic-sync-enabled');
      
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to unregister periodic sync:', error);
    return false;
  }
};

/**
 * Get periodic sync status
 */
export const getPeriodicSyncStatus = async (): Promise<PeriodicSyncStatus> => {
  const isSupported = isPeriodicSyncSupported();
  
  if (!isSupported) {
    return {
      isSupported: false,
      isRegistered: false,
      minInterval: PERIODIC_SYNC_INTERVAL,
    };
  }

  const isRegistered = await isPeriodicSyncRegistered();

  return {
    isSupported,
    isRegistered,
    minInterval: PERIODIC_SYNC_INTERVAL,
  };
};

/**
 * Listen for sync complete messages from Service Worker
 */
export const listenForSyncComplete = (callback: (success: boolean) => void): () => void => {
  // Check if serviceWorker is available
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker) {
    return () => {}; // Return no-op cleanup function
  }

  const handler = (event: MessageEvent) => {
    if (event.data && event.data.type === 'SYNC_COMPLETE') {
      callback(event.data.success);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);

  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
};

/**
 * Listen for periodic sync events from Service Worker
 */
export const listenForPeriodicSync = (callback: (timestamp: Date) => void): () => void => {
  // Check if serviceWorker is available
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker) {
    return () => {}; // Return no-op cleanup function
  }

  const handler = (event: MessageEvent) => {
    if (event.data && event.data.type === 'PERIODIC_SYNC') {
      callback(new Date(event.data.timestamp));
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);

  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
};

/**
 * Post a message to the service worker to trigger sync
 */
export const triggerManualSync = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({ type: 'TRIGGER_SYNC' });
    }
  }
};

/**
 * Get the last periodic sync time from localStorage
 */
export const getLastPeriodicSyncTime = (): Date | null => {
  const stored = localStorage.getItem('last-periodic-sync');
  return stored ? new Date(stored) : null;
};

/**
 * Set the last periodic sync time
 */
export const setLastPeriodicSyncTime = (time: Date): void => {
  localStorage.setItem('last-periodic-sync', time.toISOString());
};
